
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { API_BASE_URL } from '@/config/api';
import { useToast } from '@/hooks/use-toast';

interface RenameAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  selectedAnalysis?: {
    id: number;
    attributes: {
      subject?: string;
      patient_name?: string;
    };
  };
}

export const RenameAnalysisModal = ({ isOpen, onClose, onSuccess, selectedAnalysis }: RenameAnalysisModalProps) => {
  const [subject, setSubject] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Update state when selected analysis changes or modal opens
  useEffect(() => {
    if (selectedAnalysis && isOpen) {
      setSubject(selectedAnalysis.attributes.subject || selectedAnalysis.attributes.patient_name || "");
    }
  }, [selectedAnalysis, isOpen]);

  const handleSubjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSubject(e.target.value);
  };

  const confirmEditAnalysisName = async () => {
    if (!selectedAnalysis) return;
    
    try {
      const jwt = localStorage.getItem('token');
      
      await axios.put(`${API_BASE_URL}/analyses/${selectedAnalysis.id}`, {
        data: {
          subject: subject,
          titleEdited: true,
        },
      }, {
        headers: {
          Authorization: `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
      });

      toast({
        title: 'Análisis renombrado',
        description: 'El análisis ha sido renombrado exitosamente.',
      });

      // Close modal and call success callback immediately
      onSuccess?.();
      onClose();

      // Invalidate queries in the background without blocking the UI
      queryClient.invalidateQueries({ queryKey: ['analyses'] });
      queryClient.invalidateQueries({ queryKey: ['analysis', selectedAnalysis.id.toString()] });

    } catch (error) {
      console.error('Error al editar el análisis:', error);
      toast({
        title: 'Error',
        description: 'Hubo un error al renombrar el análisis.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editar Análisis</DialogTitle>
        </DialogHeader>
        <div className="mt-4">
          <Input 
            value={subject} 
            onChange={handleSubjectChange} 
            type="text" 
            placeholder="Ingrese el nombre del análisis" 
          />
        </div>
        <DialogFooter>
          <Button 
            variant="secondary" 
            onClick={onClose} 
            className="bg-gray-200 hover:bg-gray-300 text-black"
          >
            Cancelar
          </Button>
          <Button 
            onClick={confirmEditAnalysisName} 
            className="bg-[#257272] hover:bg-[#257272] text-white"
          >
            Guardar Cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
